import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/apiConfig";
import { clearAuthStorage } from "../utils/auth";
import "../styles/Login.css";
import thermaxLogo from "../assets/Thermax-Logo.wine01.png";

function PasswordField({ id, label, placeholder, value, onChange, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <div className="password-input-wrapper">
        <input
          type={visible ? "text" : "password"}
          id={id}
          className="form-input"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={() => setVisible((prev) => !prev)}
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {visible ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// Forced password change - reached right after login when the backend
// flags must_change_password (first login with an admin-set password, or
// after using a temporary password emailed by the forgot-password flow).
// Uses the token already issued at login.
function SetNewPasswordPage() {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(buildApiUrl("change-password/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Could not set new password.");
      }
      // Password change invalidates the old token; require a fresh login
      // with the new password rather than trying to resume mid-session.
      clearAuthStorage();
      navigate("/login", { state: { message: "Password updated. Please log in with your new password." } });
    } catch (err) {
      setError(err.message || "Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <img src={thermaxLogo} alt="Thermax" className="login-logo" />
      <div className="landing-bg-credit" aria-hidden="true">
        © Powered by Digital Team and PMO Team
      </div>
      <div className="login-container">
        <div className="login-body">
          <div className="login-form-col">
            <h1 className="login-title">Set New Password</h1>
            <p className="login-subtitle">You must set a new password before continuing.</p>
            {error && <div className="login-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <PasswordField
                id="old-password"
                label="Old Password"
                placeholder="Enter your current password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
              />
              <PasswordField
                id="new-password"
                label="New Password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <PasswordField
                id="confirm-password"
                label="Confirm Password"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? "Updating…" : "Set Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetNewPasswordPage;
